/* Exact variant image groups; the existing product selector owns URL, price and cart state. */
(() => {
    if (window.PupRingVariantGallery) {
        window.PupRingVariantGallery.initAll()
        return
    }

    class VariantGallery {
        static instances = new Map()

        static initAll(scope = document) {
            this.instances.forEach((instance, root) => {
                if (!root.isConnected) instance.destroy()
            })
            if (!window.Splide) return
            const roots = [...scope.querySelectorAll('[data-variant-gallery]')]
            if (scope.matches?.('[data-variant-gallery]')) roots.unshift(scope)
            roots.forEach(root => {
                if (this.instances.has(root)) return
                let variants
                try { variants = JSON.parse(root.querySelector('[data-gallery-variants]')?.textContent) } catch { return }
                if (!Array.isArray(variants) || !variants.every(variant => variant && /^\d+$/.test(String(variant.id)))) return
                this.instances.set(root, new this(root, variants))
            })
        }

        constructor(root, variants) {
            this.root = root
            this.variants = new Map(variants.map(variant => [String(variant.id), variant]))
            this.mainRoot = root.querySelector('.main-splide')
            this.thumbRoot = root.querySelector('.thumbs-splide')
            this.mainList = this.mainRoot.querySelector('.splide__list')
            this.thumbList = this.thumbRoot.querySelector('.splide__list')
            this.mainNodes = this.cache(this.mainList, '[data-gallery-main-cache]')
            this.thumbNodes = this.cache(this.thumbList, '[data-gallery-thumb-cache]')
            this.preloads = new Map()
            this.preloadQueue = []
            this.selection = 0
            this.popovers = []
            this.currentId = root.dataset.variantId
            this.displayedId = this.currentId
            this.currentIds = Array.from(this.mainList.children, slide => slide.dataset.mediaId)
            // Unloaded media stays blank until decoded, then reveals; alt text never paints mid-load.
            this.onMediaSettled = event => this.settle(event.target)
            this.onRevealEnd = event => {
                if (event.animationName === 'variant-gallery-reveal') event.target.closest?.('.splide__slide')?.removeAttribute('data-gallery-loaded')
            }
            root.addEventListener('load', this.onMediaSettled, true)
            root.addEventListener('error', this.onMediaSettled, true)
            root.addEventListener('animationend', this.onRevealEnd)
            ;[this.mainList, this.thumbList].forEach(list => Array.from(list.children).forEach(slide => {
                const img = slide.querySelector('img')
                if (img?.complete && !img.naturalWidth) this.mark(slide)
            }))
            this.updateSizes()
            if (window.ResizeObserver) {
                this.resizeObserver = new window.ResizeObserver(() => this.updateSizes())
                this.resizeObserver.observe(this.mainRoot)
            }
            const count = this.currentIds.length
            const i18n = {
                prev: root.dataset.labelPrevious,
                next: root.dataset.labelNext,
                slideLabel: root.dataset.labelSlide.replace('[x]', '%s').replace('[total]', '%s'),
                slideX: root.dataset.labelSlide.replace('[x]', '%s').replace('[total]', count)
            }
            this.main = new window.Splide(this.mainRoot, {
                ...this.mainOptions(count), pagination: false, speed: 300, i18n,
                direction: document.documentElement.getAttribute('dir') || 'ltr'
            })
            this.thumbs = new window.Splide(this.thumbRoot, {
                ...this.thumbOptions(count), perPage: 5, gap: '.5rem', pagination: false,
                isNavigation: true, speed: 300, i18n,
                direction: document.documentElement.getAttribute('dir') || 'ltr'
            })
            this.main.sync(this.thumbs)
            this.main.mount()
            this.thumbs.mount()
            this.main.on('move', index => this.prime(index))
            this.labelThumbnails()
            this.prime(0)
            this.updateSizes()
            this.initPopovers()
            this.stickGallery()
            if (window.GLightbox && [...this.mainNodes.values()].some(slide => slide.querySelector('.glightbox'))) {
                this.lightbox = window.GLightbox({ selector: '#' + root.id + ' .main-splide .glightbox', loop: false })
                this.lightbox.on('open', () => {
                    for (const [selector, label] of [['.gprev', root.dataset.labelPrevious], ['.gnext', root.dataset.labelNext], ['.gclose', root.dataset.labelClose]]) {
                        this.lightbox.modal?.querySelector(selector)?.setAttribute('aria-label', label)
                    }
                })
                this.lightbox.on('slide_changed', ({ current }) => {
                    if (current && !this.destroyed && !this.loading) this.main.go(current.index)
                })
            }
            this.legacySource = root.dataset.galleryVariantSource === 'legacy'
            this.legacyScope = this.legacySource ? root.closest('.shopify-section') : null
            this.onVariant = event => {
                if (event.detail?.product_id && String(event.detail.product_id) !== root.dataset.productId) return
                if (this.legacySource && !this.matchesLegacyVariant(event.detail)) return
                this.select(event.detail?.id)
            }
            window.addEventListener('variantChange.ecomify.product', this.onVariant)
            if (this.legacyScope) {
                // Motion can announce a variant before updating its master select.
                // Follow only committed form state, without changing that state.
                this.onLegacyMaster = event => {
                    const master = this.legacyMaster()
                    if (master && event.target === master && this.matchesLegacyVariant({ id: master.value })) this.select(master.value)
                }
                this.onLegacyVariant = event => {
                    if (event.target !== document && event.target?.closest?.('.shopify-section') !== this.legacyScope) return
                    if (event.detail?.product_id && String(event.detail.product_id) !== root.dataset.productId) return
                    if (this.matchesLegacyVariant(event.detail?.variant)) this.select(event.detail.variant.id)
                }
                this.legacyScope.addEventListener('change', this.onLegacyMaster)
                document.addEventListener('variant:change', this.onLegacyVariant)
            }
            this.productScope = root.closest('#product-template') || root.closest('.shopify-section') || root
            this.onIntent = event => this.prepareIntent(event.target)
            this.intentEvents = ['pointerover', 'pointerdown', 'focusin']
            this.intentEvents.forEach(type => this.productScope.addEventListener(type, this.onIntent))
            // Start only two distinct neighboring heroes, after the initial page load.
            this.onPageLoad = () => {
                this.idleTimer = window.setTimeout(() => {
                    if (window.requestIdleCallback) this.idleCallback = window.requestIdleCallback(() => this.warmNeighbors())
                    else this.warmNeighbors()
                }, 1200)
            }
            if (document.readyState === 'complete') this.onPageLoad()
            else window.addEventListener('load', this.onPageLoad, { once: true })
        }

        legacyMaster() {
            if (!this.legacyScope) return
            const masters = [...this.legacyScope.querySelectorAll('select[name="id"][data-product-select]')]
                .filter(master => master.closest('.shopify-section') === this.legacyScope
                    && master.closest('[data-product-id]')?.dataset.productId === this.root.dataset.productId)
            return masters.length === 1 ? masters[0] : undefined
        }

        matchesLegacyVariant(variant) {
            if (!variant || (variant.product_id && String(variant.product_id) !== this.root.dataset.productId)) return false
            const master = this.legacyMaster()
            const id = String(variant.id)
            return Boolean(master && this.variants.has(id) && String(master.value) === id)
        }

        mainOptions(count) {
            return {
                rewind: count > 1, rewindByDrag: count > 1, drag: count > 1,
                arrows: count > 1 && this.root.dataset.arrows === 'main-slider',
                mediaQuery: 'min',
                breakpoints: {
                    0: { padding: { right: count > 1 && this.root.dataset.showThumbsMobile === 'false' ? '4rem' : 0 } },
                    992: { padding: 0 }
                }
            }
        }

        thumbOptions(count) {
            return {
                arrows: count > 5 && this.root.dataset.arrows === 'thumbs-slider',
                rewind: count > 1, rewindByDrag: count > 1, drag: count > 1,
                focus: count > 5 ? 'center' : 0,
                i18n: { slideX: this.root.dataset.labelSlide.replace('[x]', '%s').replace('[total]', count) }
            }
        }

        stickGallery() {
            if (!window.matchMedia('(min-width: 992px)').matches) return
            const wrapper = this.root.closest('.product-gallery-wrapper')
            if (!wrapper) return
            this.stickyWrapper = wrapper
            this.previousSticky = { position: wrapper.style.position, top: wrapper.style.top, zIndex: wrapper.style.zIndex }
            const navbar = document.querySelector('#navbar-desktop')?.clientHeight || 0
            const announcement = document.querySelector('[id*="__announcement-bar"].sticky-top')?.clientHeight || 0
            Object.assign(wrapper.style, { position: 'sticky', top: navbar + announcement + 20 + 'px', zIndex: '1' })
        }

        updateSizes() {
            // Measure the image column itself, including crop and mobile next-slide padding.
            // A broken image collapses to its alt text, so it is never the reference.
            const displayed = [...this.mainList.querySelectorAll('img')].find(img => !img.complete || img.naturalWidth)
            const displayedWidth = displayed?.getBoundingClientRect().width
            const imageWidth = Math.ceil(displayedWidth || this.mainRoot.getBoundingClientRect().width)
            if (!imageWidth) return
            if (imageWidth === this.imageWidth) return
            this.imageWidth = imageWidth
            this.mainNodes.forEach(slide => slide.querySelector('img').sizes = imageWidth + 'px')
            this.preloads.forEach((record, id) => {
                if (record.state === 'ready') this.preloads.delete(id)
                else record.img.sizes = imageWidth + 'px'
            })
        }

        allowLookAhead() {
            const connection = window.navigator?.connection
            return !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType)
        }

        prime(index) {
            Array.from(this.mainList.children).forEach((slide, i) => {
                const img = slide.querySelector('img')
                const ahead = this.allowLookAhead() && i > index && i <= index + 2
                img.setAttribute('fetchpriority', i === index ? 'high' : ahead ? 'low' : 'auto')
                img.setAttribute('loading', i === index || ahead ? 'eager' : 'lazy')
            })
        }

        mediaIds(variant) {
            const configured = Array.isArray(variant.media) ? variant.media : []
            const ids = [...new Set(configured.filter(id => ['string', 'number'].includes(typeof id) && /^(?:[1-9]\d*)$/.test(String(id))).map(String))]
                .filter(id => this.mainNodes.has(id) && this.thumbNodes.has(id))
            const featured = String(variant.featured)
            return ids.length ? ids : this.mainNodes.has(featured) && this.thumbNodes.has(featured) ? [featured] : []
        }

        prepareIntent(target) {
            if (this.destroyed || !this.allowLookAhead() || !target?.closest || !this.productScope.contains(target)) return
            const thumbnail = target.closest('.thumbs-splide [data-media-id]')
            if (thumbnail && this.root.contains(thumbnail)) return this.preload(thumbnail.dataset.mediaId)
            const label = target.closest('label[for]')
            const input = label
                ? [...this.productScope.querySelectorAll('.product-option')].find(option => option.id === label.htmlFor)
                : target.closest('.product-option')
            if (!input?.matches('.product-option') || !this.productScope.contains(input)) return
            if (input.dataset.productId && input.dataset.productId !== this.root.dataset.productId) return
            const form = input.closest('form[data-variants]')
            if (!form) return
            const selected = Array.from(form.querySelectorAll('.product-option'))
                .filter(option => option.type !== 'radio' || (option.name === input.name ? option === input : option.checked))
                .map(option => option.value)
            let variants
            try { variants = JSON.parse(form.dataset.variants) } catch { return }
            if (!Array.isArray(variants)) return
            const prepare = values => {
                const match = variants.find(variant => variant && Array.isArray(variant.options) && JSON.stringify(variant.options) === JSON.stringify(values))
                const variant = this.variants.get(String(match?.id))
                if (variant) this.preload(this.mediaIds(variant)[0])
            }
            if (input.tagName === 'SELECT') {
                // Native option popups do not dispatch option hover/focus events.
                const position = [...form.querySelectorAll('.product-option')]
                    .filter(option => option.type !== 'radio' || option.checked).indexOf(input)
                for (const offset of [1, -1]) {
                    const option = input.options[input.selectedIndex + offset]
                    if (option && !option.disabled) prepare(selected.map((value, index) => index === position ? option.value : value))
                }
            } else prepare(selected)
        }

        warmNeighbors() {
            if (this.destroyed || !this.allowLookAhead() || this.loading) return
            const variants = [...this.variants.values()]
            const start = variants.findIndex(variant => String(variant.id) === this.currentId)
            const heroes = new Set()
            for (let offset = 1; offset < variants.length && heroes.size < 2; offset++) {
                const id = this.mediaIds(variants[(start + offset) % variants.length])[0]
                if (id && !this.currentIds.includes(id)) heroes.add(id)
            }
            heroes.forEach(id => this.preload(id))
        }

        preload(id) {
            if (this.destroyed || !this.allowLookAhead()) return
            const source = this.mainNodes.get(id)?.querySelector('img')
            if (!source || (source.complete && source.naturalWidth) || this.preloads.has(id)) return
            // Two speculative requests in flight and two queued; pointer sweeps cannot
            // enqueue the entire product. A requested hero bypasses this queue.
            this.preloadQueue = [id, ...this.preloadQueue.filter(key => key !== id)].slice(0, 2)
            this.drainPreloads()
        }

        drainPreloads() {
            if (this.destroyed || this.loading || !this.allowLookAhead()) return
            while (this.preloadQueue.length && [...this.preloads.values()].filter(record => record.state === 'loading').length < 2) {
                this.loadImage(this.preloadQueue.shift(), false)
            }
        }

        loadImage(id, requested) {
            const existing = this.preloads.get(id)
            if (existing) {
                if (requested) {
                    existing.requested = true
                    existing.img.setAttribute('fetchpriority', 'high')
                }
                return existing
            }
            const source = this.mainNodes.get(id)?.querySelector('img')
            if (!source) return
            const cached = source.complete && source.naturalWidth > 0
            const img = cached ? source : new window.Image()
            for (const attribute of source.attributes) {
                if (!['src', 'srcset', 'sizes', 'loading', 'fetchpriority', 'data-gallery-src', 'data-gallery-srcset'].includes(attribute.name)) img.setAttribute(attribute.name, attribute.value)
            }
            img.setAttribute('fetchpriority', requested ? 'high' : 'low')
            img.setAttribute('loading', 'eager')
            img.setAttribute('decoding', 'async')
            const record = { img, state: 'loading', requested }
            this.preloads.set(id, record)
            record.promise = new Promise(resolve => {
                const finish = (ready, cancelled) => {
                    if (record.state !== 'loading') return
                    window.clearTimeout(record.timer)
                    img.onload = img.onerror = null
                    record.state = ready ? 'ready' : 'failed'
                    if (!ready) {
                        // A cached slide belongs to the gallery; cancellation must
                        // not erase its source during a rapid switch or unload.
                        if (cancelled && !cached) {
                            img.removeAttribute('srcset')
                            img.removeAttribute('src')
                        }
                        this.preloads.delete(id)
                    }
                    // An errored or slow requested hero still goes to its slide,
                    // which keeps its own loading or error state.
                    resolve(cancelled ? null : img)
                    this.drainPreloads()
                }
                record.cancel = () => finish(false, true)
                img.onerror = () => finish(false)
                img.onload = () => {
                    const candidate = img.currentSrc
                    const done = () => {
                        if (record.state !== 'loading' || !img.complete) return
                        if (img.currentSrc !== candidate) return img.onload()
                        finish(img.naturalWidth > 0)
                    }
                    // A decode rejection may still leave usable pixels (for example
                    // during a resize); an actual load failure never reveals old media.
                    if (img.decode) img.decode().then(done, done)
                    else done()
                }
                record.timer = window.setTimeout(() => finish(false, !record.requested), 15000)
            })
            if (cached) img.onload()
            else {
                img.sizes = source.sizes
                const srcset = source.getAttribute('srcset') ?? source.getAttribute('data-gallery-srcset')
                if (srcset) img.srcset = srcset
                img.src = source.getAttribute('src') ?? source.getAttribute('data-gallery-src')
            }
            // Retain at most eight prepared images beyond the existing slide cache.
            for (const [key, value] of this.preloads) {
                if (this.preloads.size <= 8) break
                if (key !== id && value.state === 'ready') this.preloads.delete(key)
            }
            return record
        }

        watch(list) {
            Array.from(list.children).forEach(slide => {
                this.resume(slide.querySelector('img'))
                slide.removeAttribute('data-gallery-loaded')
                this.mark(slide)
            })
        }

        mark(slide) {
            // A failed image keeps its slide's shape as a placeholder instead of
            // collapsing to its alt text; loading again clears it.
            const img = slide.querySelector('img')
            const failed = Boolean(img?.complete && !img.naturalWidth)
            slide.toggleAttribute('data-gallery-loading', Boolean(img && !img.complete))
            slide.toggleAttribute('data-gallery-error', failed)
            const width = Number(img?.getAttribute('width'))
            const height = Number(img?.getAttribute('height'))
            if (failed && width > 0 && height > 0) img.style.setProperty('--variant-gallery-ratio', width + ' / ' + height)
        }

        suspend(img) {
            // A committed hero still downloading when the shopper moves on stops
            // competing with the next variant; showing it again restarts it.
            if (!img || img.complete || img.hasAttribute('data-gallery-src')) return
            img.setAttribute('data-gallery-src', img.getAttribute('src') || '')
            if (img.hasAttribute('srcset')) img.setAttribute('data-gallery-srcset', img.getAttribute('srcset'))
            img.removeAttribute('srcset')
            img.removeAttribute('src')
            const slide = img.closest('.splide__slide')
            if (slide && this.mainList.contains(slide)) this.mark(slide)
        }

        resume(img) {
            if (!img?.hasAttribute('data-gallery-src')) return false
            if (img.hasAttribute('data-gallery-srcset')) img.setAttribute('srcset', img.getAttribute('data-gallery-srcset'))
            img.setAttribute('src', img.getAttribute('data-gallery-src'))
            img.removeAttribute('data-gallery-src')
            img.removeAttribute('data-gallery-srcset')
            return true
        }

        settle(img) {
            const slide = img?.closest?.('.splide__slide')
            if (!slide || slide.querySelector('img') !== img || !this.root.contains(slide)) return
            if (!img.naturalWidth) return this.mark(slide)
            slide.removeAttribute('data-gallery-error')
            // A hero that finishes after the handover no longer needs a retry.
            if (!this.loading && img === this.mainList.querySelector('img')) this.loadFailed = false
            if (!slide.hasAttribute('data-gallery-loading')) return
            const reveal = () => {
                if (this.destroyed || !slide.hasAttribute('data-gallery-loading')) return
                slide.removeAttribute('data-gallery-loading')
                slide.setAttribute('data-gallery-loaded', '')
            }
            if (img.naturalWidth && typeof img.decode === 'function') img.decode().then(reveal, reveal)
            else reveal()
        }

        cache(list, selector) {
            const template = this.root.querySelector(selector)
            return new Map([...template.content.children, ...list.children].map(node => [node.dataset.mediaId, node]))
        }

        labelThumbnails() {
            Array.from(this.thumbList.children).forEach((slide, index, slides) => {
                slide.setAttribute('role', 'button')
                slide.setAttribute('aria-label', this.root.dataset.labelSlide.replace('[x]', index + 1).replace('[total]', slides.length))
                const mainSlide = this.mainList.children[index]
                if (mainSlide?.id) slide.setAttribute('aria-controls', mainSlide.id)
            })
        }

        announce(variant, count) {
            const label = count
                ? this.root.dataset.labelSlide.replace('[x]', this.main.index + 1).replace('[total]', count)
                : this.root.dataset.labelUnavailable
            this.root.querySelector('[data-gallery-status]').textContent = variant.title + ': ' + label
        }

        initPopovers() {
            if (!window.bootstrap?.Popover) return
            this.popovers = [...this.mainList.querySelectorAll('[data-bs-toggle="popover"]')]
                .map(node => window.bootstrap.Popover.getOrCreateInstance(node))
        }

        disposePopovers() {
            this.popovers.forEach(popover => popover.dispose())
            this.popovers = []
        }

        select(id) {
            if (this.destroyed) return
            const key = String(id)
            const variant = this.variants.get(key)
            if (!variant || (key === this.currentId && !this.loadFailed)) return
            const ids = this.mediaIds(variant)
            const token = ++this.selection
            const retry = this.loadFailed
            this.loadFailed = false
            this.currentId = key
            this.root.dataset.variantId = key
            this.preloadQueue = []
            if (this.pendingHero && this.pendingHero !== ids[0]) {
                const previous = this.preloads.get(this.pendingHero)
                if (previous?.state === 'loading') previous.cancel()
            }
            this.pendingHero = ids[0]
            if (!ids.includes(this.currentIds[0])) this.suspend(this.mainNodes.get(this.currentIds[0])?.querySelector('img'))
            const hero = this.mainNodes.get(ids[0])?.querySelector('img')
            const broken = retry && hero?.complete && !hero.naturalWidth
            if (!broken && ids.length === this.currentIds.length && ids.every((mediaId, index) => mediaId === this.currentIds[index])) {
                Array.from(this.mainList.children).forEach(slide => {
                    if (this.resume(slide.querySelector('img'))) this.mark(slide)
                })
                this.setLoading(false)
                this.loadingFocus = null
                this.pendingHero = null
                this.loadFailed = retry && !hero?.naturalWidth
                this.displayedId = key
                this.initPopovers()
                this.announce(variant, ids.length)
                return
            }
            const prepared = this.preloads.get(ids[0])
            if (!hero || (hero.complete && hero.naturalWidth && !hero.decode) || prepared?.state === 'ready') {
                if (prepared?.state === 'ready') hero.replaceWith(prepared.img)
                this.commit(variant, ids)
                return
            }
            this.setLoading(true)
            this.lightbox?.close()
            this.disposePopovers()
            const record = this.loadImage(ids[0], true)
            return record.promise.then(img => {
                if (this.destroyed || token !== this.selection) return
                // A failed or slow hero never reads as an unavailable product;
                // reselecting the variant retries it.
                this.loadFailed = record.state !== 'ready'
                if (img) hero.replaceWith(img)
                this.commit(variant, ids)
            })
        }

        setLoading(loading) {
            if (loading === Boolean(this.loading)) return
            this.loading = loading
            if (loading) {
                const active = document.activeElement
                this.loadingFocus = active && this.root.contains(active) ? active : null
                this.loadingStyles = [this.mainRoot, this.thumbRoot].map(node => ({
                    opacity: node.style.opacity, inert: node.inert, ariaHidden: node.getAttribute('aria-hidden')
                }))
                this.root.setAttribute('aria-busy', 'true')
                this.root.dataset.galleryLoading = 'true'
                this.root.querySelector('[data-gallery-status]').textContent = ''
            } else {
                this.root.removeAttribute('aria-busy')
                delete this.root.dataset.galleryLoading
            }
            ;[this.mainRoot, this.thumbRoot].forEach((node, index) => {
                const previous = this.loadingStyles[index]
                node.style.opacity = loading ? '0.65' : previous.opacity
                node.inert = loading || previous.inert
                if (loading) node.setAttribute('aria-hidden', 'true')
                else if (previous.ariaHidden === null) node.removeAttribute('aria-hidden')
                else node.setAttribute('aria-hidden', previous.ariaHidden)
            })
        }

        commit(variant, ids) {
            const active = document.activeElement
            const focus = this.loadingFocus && (active === document.body || active === this.loadingFocus) ? this.loadingFocus : active
            const hadGalleryFocus = focus && (this.mainRoot.contains(focus) || this.thumbRoot.contains(focus))
            const focusedId = hadGalleryFocus && focus.closest('[data-media-id]')?.dataset.mediaId
            const thumbnailFocus = hadGalleryFocus && this.thumbRoot.contains(focus)
            this.setLoading(false)
            this.loadingFocus = null
            this.pendingHero = null
            this.displayedId = String(variant.id)
            this.currentIds = ids
            this.lightbox?.close()
            this.disposePopovers()
            // Move.cancel() stops the transition but leaves Splide in MOVING.
            // Reset both controllers before refresh can clamp the old indices.
            for (const carousel of [this.main, this.thumbs]) {
                carousel.Components.Move.cancel()
                carousel.Components.Scroll.cancel()
                carousel.state.set(window.Splide.STATES.IDLE)
                carousel.Components.Controller.setIndex(0)
            }
            this.mainList.replaceChildren(...ids.map(mediaId => this.mainNodes.get(mediaId)))
            this.thumbList.replaceChildren(...ids.map(mediaId => this.thumbNodes.get(mediaId)))
            // Showing a variant again retries its failed thumbnails along with its hero.
            this.thumbList.querySelectorAll('img').forEach(img => {
                if (img.complete && !img.naturalWidth && img.getAttribute('src')) img.setAttribute('src', img.getAttribute('src'))
            })
            this.watch(this.mainList)
            this.watch(this.thumbList)
            this.prime(0)
            this.thumbRoot.hidden = ids.length <= 1
            this.mainRoot.hidden = !ids.length
            this.root.querySelector('[data-gallery-empty]').hidden = ids.length > 0
            this.main.options = this.mainOptions(ids.length)
            this.thumbs.options = this.thumbOptions(ids.length)
            this.main.refresh()
            this.thumbs.refresh()
            this.labelThumbnails()
            this.lightbox?.reload()
            this.initPopovers()
            this.imageWidth = null
            this.updateSizes()
            this.announce(variant, ids.length)
            if (hadGalleryFocus) {
                const retainedThumbnail = this.thumbNodes.get(focusedId)
                const mediaId = thumbnailFocus && ids.includes(focusedId) && retainedThumbnail?.getAttribute('aria-hidden') !== 'true'
                    ? focusedId : ids[0]
                const focusTarget = thumbnailFocus && ids.length > 1
                    ? this.thumbNodes.get(mediaId)
                    : this.mainNodes.get(mediaId)?.querySelector('a')
                if (focusTarget) focusTarget.focus({ preventScroll: true })
                else {
                    this.root.setAttribute('tabindex', '-1')
                    this.root.focus({ preventScroll: true })
                }
            }
            this.drainPreloads()
        }

        destroy() {
            if (this.destroyed) return
            this.destroyed = true
            this.selection++
            this.root.dataset.variantId = this.displayedId
            this.setLoading(false)
            this.loadingFocus = null
            window.removeEventListener('load', this.onPageLoad)
            window.clearTimeout(this.idleTimer)
            if (this.idleCallback !== undefined) window.cancelIdleCallback(this.idleCallback)
            window.removeEventListener('variantChange.ecomify.product', this.onVariant)
            this.legacyScope?.removeEventListener('change', this.onLegacyMaster)
            if (this.onLegacyVariant) document.removeEventListener('variant:change', this.onLegacyVariant)
            this.intentEvents.forEach(type => this.productScope.removeEventListener(type, this.onIntent))
            this.root.removeEventListener('load', this.onMediaSettled, true)
            this.root.removeEventListener('error', this.onMediaSettled, true)
            this.root.removeEventListener('animationend', this.onRevealEnd)
            this.resizeObserver?.disconnect()
            this.preloadQueue = []
            this.preloads.forEach(record => { if (record.state === 'loading') record.cancel() })
            this.preloads.clear()
            this.mainList.querySelectorAll('img').forEach(img => this.resume(img))
            this.lightbox?.destroy()
            this.disposePopovers()
            this.main.destroy()
            this.thumbs.destroy()
            if (this.stickyWrapper) Object.assign(this.stickyWrapper.style, this.previousSticky)
            // Retain detached media if the same section node is mounted again.
            for (const [nodes, list, selector] of [
                [this.mainNodes, this.mainList, '[data-gallery-main-cache]'],
                [this.thumbNodes, this.thumbList, '[data-gallery-thumb-cache]']
            ]) {
                const cache = this.root.querySelector(selector).content
                nodes.forEach(node => { if (node.parentNode !== list) cache.appendChild(node) })
            }
            VariantGallery.instances.delete(this.root)
        }
    }

    window.PupRingVariantGallery = VariantGallery
    VariantGallery.initAll()
    document.addEventListener('DOMContentLoaded', () => VariantGallery.initAll(), { once: true })
    window.addEventListener('load', () => VariantGallery.initAll(), { once: true })
    document.addEventListener('shopify:section:load', event => VariantGallery.initAll(event.target))
    document.addEventListener('shopify:section:unload', event => {
        VariantGallery.instances.forEach((instance, root) => {
            if (event.target.contains(root)) instance.destroy()
        })
    })
})()
